import getCategory from "../../../utils/getCategory";
import getWeightCategory from "../../../utils/getWeightCategory";

import {
  AGE_CATEGORY_ORDER,
  WEIGHT_EXCLUDED_LABELS,
} from "../data/peseeData";

export const normalizeText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const parseDisciplines = (raw) => {
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : ["Judo"];
    } catch {
      return ["Judo"];
    }
  }

  return ["Judo"];
};

export const parseWeightInput = (value) => {
  if (value === "" || value == null) return null;

  const normalized = String(value).replace(",", ".").trim();
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 300) {
    return {
      valid: false,
      value: null,
      message: "Poids invalide (0–300 kg).",
    };
  }

  return {
    valid: true,
    value: numeric,
    message: "",
  };
};

export const formatWeight = (value) => {
  if (value === "" || value == null) return "";
  const parsed = Number(String(value).replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return parsed.toFixed(1);
};

export const getBirthYear = (date) => {
  if (!date) return "";
  const year = new Date(date).getFullYear();
  return Number.isFinite(year) ? year : "";
};

export const enrichCompetiteurs = ({
  judokas = [],
  settings,
  disciplinesList = [],
}) => {
  const country = settings?.cate_country || "France";
  const defaultDiscipline = disciplinesList?.[0] || "Judo";

  return judokas.map((competiteur) => {
    const discipline = competiteur.discipline_competiteur || defaultDiscipline;
    const ageCat = getCategory(
      competiteur.ddn_competiteur,
      country,
      discipline
    );

    const weightCat = getWeightCategory(
      competiteur.genre_competiteur,
      ageCat,
      competiteur.poids_competiteur,
      country,
      discipline
    );

    return {
      ...competiteur,
      _discipline: discipline,
      _ageCat: ageCat,
      _weightCat: weightCat,
      _birthYear: getBirthYear(competiteur.ddn_competiteur),
      _club: String(competiteur.club_competiteur || "").trim(),
    };
  });
};

export const matchMulti = (selected = [], value) => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.map(String).includes(String(value));
};

export const filterCompetiteurs = ({ rows = [], filters, search = "" }) => {
  const q = normalizeText(search);

  return rows.filter((competiteur) => {
    const fullText = normalizeText(
      [
        competiteur.nom_competiteur,
        competiteur.prenom_competiteur,
        competiteur.club_competiteur,
        competiteur.licence_competiteur,
      ].join(" ")
    );

    const matchSearch = !q || fullText.includes(q);

    const matchPresence =
      filters.presence === "all" ||
      (filters.presence === "present" && competiteur.is_present) ||
      (filters.presence === "absent" && !competiteur.is_present);

    const matchGender =
      filters.gender === "all" ||
      String(competiteur.genre_competiteur || "").toUpperCase() ===
        filters.gender;

    const matchDiscipline =
      filters.discipline === "all" ||
      competiteur._discipline === filters.discipline;

    const matchClub =
      filters.club === "all" || competiteur._club === filters.club;

    const matchAge = matchMulti(filters.ageCategories, competiteur._ageCat);
    const matchWeight = matchMulti(
      filters.weightCategories,
      competiteur._weightCat
    );
    const matchYear = matchMulti(filters.years, competiteur._birthYear);

    return (
      matchSearch &&
      matchPresence &&
      matchGender &&
      matchDiscipline &&
      matchClub &&
      matchAge &&
      matchWeight &&
      matchYear
    );
  });
};

export const uniqueSorted = (values = []) =>
  Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
  );

export const sortAgeCategories = (values = []) => {
  const rank = (label) => {
    const index = AGE_CATEGORY_ORDER.indexOf(label);
    return index === -1 ? 999 : index;
  };

  return Array.from(new Set(values.filter(Boolean)))
    .filter((label) => label !== "Inconnu")
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );
};

export const sortWeightLabels = (labels = []) => {
  const normalizeForSort = (label) => {
    const match = String(label || "").match(/^([+-]?)(\d+(?:\.\d+)?)\s*kg$/i);

    if (!match) {
      return {
        type: "text",
        value: String(label || "").toLowerCase(),
      };
    }

    const sign = match[1] || "-";
    const number = parseFloat(match[2]);

    return {
      type: "number",
      value: sign === "+" ? 1000 + number : number,
    };
  };

  return Array.from(new Set(labels.filter(Boolean)))
    .filter((label) => !WEIGHT_EXCLUDED_LABELS.includes(label))
    .sort((a, b) => {
      const A = normalizeForSort(a);
      const B = normalizeForSort(b);

      if (A.type !== B.type) return A.type === "number" ? -1 : 1;
      if (A.value !== B.value) return A.value > B.value ? 1 : -1;

      return String(a).localeCompare(String(b), "fr", {
        sensitivity: "base",
      });
    });
};

export const getOptionsFromRows = (rows = []) => {
  return {
    clubs: uniqueSorted(rows.map((row) => row._club)),
    years: Array.from(new Set(rows.map((row) => row._birthYear).filter(Boolean))).sort(
      (a, b) => Number(a) - Number(b)
    ),
    ageCategories: sortAgeCategories(rows.map((row) => row._ageCat)),
    weightCategories: sortWeightLabels(rows.map((row) => row._weightCat)),
  };
};

export const safeFileName = (name) =>
  String(name || "export")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

export const downloadCSV = (rows, filename = "competiteurs.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);

  const escape = (value) =>
    `"${String(value ?? "").replaceAll('"', '""').replaceAll("\n", " ")}"`;

  const body = rows
    .map((row) => headers.map((header) => escape(row[header])).join(";"))
    .join("\n");

  const csv = `${headers.join(";")}\n${body}`;
  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

export const buildCsvRows = (rows = []) =>
  rows.map((competiteur) => ({
    ID: competiteur.id,
    Nom: competiteur.nom_competiteur || "",
    Prenom: competiteur.prenom_competiteur || "",
    Club: competiteur._club || "",
    Licence: competiteur.licence_competiteur || "",
    Genre: String(competiteur.genre_competiteur || "").toUpperCase(),
    Discipline: competiteur._discipline || "",
    Presence: competiteur.is_present ? "Présent" : "Absent",
    DDN: competiteur.ddn_competiteur
      ? new Date(competiteur.ddn_competiteur).toLocaleDateString("fr-FR")
      : "",
    Annee_naissance: competiteur._birthYear ?? "",
    Categorie_age: competiteur._ageCat ?? "",
    Poids_kg: competiteur.poids_competiteur ?? "",
    Categorie_poids: competiteur._weightCat ?? "",
  }));

export const genreKey = (gender) => {
  const value = normalizeText(gender);

  if (["m", "masculin", "h", "homme"].includes(value)) return "M";
  if (["f", "feminin", "femme"].includes(value)) return "F";

  return "";
};

export const getGradeClasses = (grade) => {
  const value = normalizeText(grade);

  if (!value) {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  }

  if (value.includes("jaune-orange")) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  }

  if (value.includes("orange-verte")) {
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
  }

  if (value.includes("verte-bleue")) {
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
  }

  if (value.includes("bleue-marron")) {
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
  }

  if (value.includes("blanche") || value.includes("blanc")) {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  }

  if (value.includes("jaune")) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  }

  if (value.includes("orange")) {
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
  }

  if (value.includes("verte")) {
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
  }

  if (value.includes("bleue") || value.includes("bleu")) {
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
  }

  if (value.includes("marron") || value.includes("brun")) {
    return "bg-amber-900 text-amber-100 dark:bg-amber-900/40 dark:text-amber-100";
  }

  if (value.includes("dan")) {
    return "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900";
  }

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};